/*
 * 模块: DataManagementDetail
 * 职责: 数据管理详情页（热门搜索词、搜索查询绩效、关键词搜索结果）
 * 依赖: custom hooks, 子组件
 * 导出: DataManagementDetail
 * 限制: 文件<280行, 函数<30行
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Empty, Card, Breadcrumb, Space, Button, Radio } from 'antd';
import { ArrowLeftOutlined, HomeOutlined, DatabaseOutlined } from '@ant-design/icons';
import { PeriodManagementSection, SearchPerformanceSection } from '../components/SKUDetail';
import { useSKUData } from '../hooks/useSKUData';
import { KeywordSearchImport } from './DataManagement/components/KeywordSearchImport';
import './SKUDetail.css';

/**
 * 数据管理详情页
 * 职责：管理各类数据（热门搜索词、搜索查询绩效、关键词搜索结果）
 */
export const DataManagementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<'hot-search' | 'search-performance' | 'keyword-search'>('hot-search');

  // 使用自定义Hooks管理数据
  const {
    loading,
    sku,
    periods,
    loadData,
    reload,
  } = useSKUData(id);

  // 初始加载
  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  // 返回列表
  const handleBack = () => {
    navigate('/data-management');
  };

  // 加载状态
  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  // SKU不存在
  if (!sku) {
    return <Empty description="SKU不存在" />;
  }

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* 面包屑导航 */}
      <Breadcrumb
        style={{ marginBottom: '16px' }}
        items={[
          {
            title: <HomeOutlined />,
          },
          {
            title: (
              <span onClick={() => handleBack()} style={{ cursor: 'pointer' }}>
                <DatabaseOutlined />
                <span style={{ marginLeft: '4px' }}>数据管理</span>
              </span>
            ),
          },
          {
            title: `${sku.name} (${sku.asin})`,
          },
        ]}
      />

      {/* 页面头部 */}
      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                返回列表
              </Button>
              <h2 style={{ margin: 0 }}>数据管理</h2>
            </Space>
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>
            <strong>{sku.name}</strong> ({sku.asin})
          </div>
        </Space>
      </Card>

      {/* 数据源切换 */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: 8 }}>
          <strong>数据类型：</strong>
        </div>
        <Radio.Group
          value={dataSource}
          onChange={(e) => setDataSource(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="hot-search">热门搜索词数据</Radio.Button>
          <Radio.Button value="search-performance">搜索查询绩效数据</Radio.Button>
          <Radio.Button value="keyword-search">关键词搜索结果</Radio.Button>
        </Radio.Group>
      </Card>

      {/* 数据管理区域 */}
      {dataSource === 'hot-search' && (
        <PeriodManagementSection
          periods={periods}
          skuId={Number(id)}
          onPeriodUploaded={reload}
        />
      )}
      {dataSource === 'search-performance' && (
        <SearchPerformanceSection skuId={Number(id)} />
      )}
      {dataSource === 'keyword-search' && (
        <KeywordSearchImport />
      )}
    </div>
  );
};

export default DataManagementDetail;
